import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShoppingCart, Scale, MessageCircle, Mail, BellRing, Check, X, Loader2, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KIND = {
  commande: { icon: ShoppingCart, label: 'Bon de commande', tone: 'bg-orange-50 border-orange-200 text-orange-900' },
  portion: { icon: Scale, label: 'Portion', tone: 'bg-[var(--p-9be38c)] border-[var(--p-a8dda0)] text-[var(--encre)]' },
  message_equipe: { icon: MessageCircle, label: 'Message équipe', tone: 'bg-[var(--p-a9d3f2)] border-blue-200 text-blue-900' },
  message_fournisseur: { icon: Mail, label: 'Message fournisseur', tone: 'bg-[var(--p-d6b6f2)] border-violet-200 text-violet-900' },
  rappel_taches: { icon: BellRing, label: 'Rappel tâches', tone: 'bg-[var(--p-fde3d8)] border-[var(--p-efa3b1)] text-[var(--encre)]' },
};

function openLinks(result) {
  const links = [result?.whatsapp, result?.mailto, ...(result?.links || []).map((l) => l.whatsapp)].filter(Boolean);
  links.slice(0, 3).forEach((href, i) => setTimeout(() => window.open(href, '_blank', 'noopener'), i * 350));
}

export const ProposalCard = ({ proposal, onChange }) => {
  const [busy, setBusy] = useState(null);
  const [p, setP] = useState(proposal);
  const meta = KIND[p.kind] || KIND.commande;
  const Icon = meta.icon;
  const done = p.status !== 'pending';

  const act = async (verb) => {
    setBusy(verb);
    try {
      const r = await axios.post(`${API}/ai/proposals/${p.proposal_id}/${verb}`, {}, { withCredentials: true });
      const next = { ...p, status: r.data.status, result: r.data.result };
      setP(next);
      onChange?.(next);
      if (verb === 'confirm') {
        toast.success(r.data.result?.message || 'Action confirmée');
        openLinks(r.data.result);
      } else toast('Proposition ignorée');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action impossible');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={`mt-3 rounded-2xl border p-3 ${meta.tone}`} data-testid={`proposal-card-${p.proposal_id}`}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide opacity-70">
        <Icon size={13} /> {meta.label}
        {done && <span className="ml-auto normal-case tracking-normal" data-testid={`proposal-status-${p.proposal_id}`}>{p.status === 'confirmed' ? '✓ Confirmée' : 'Ignorée'}{p.confirmed_by ? ` · ${p.confirmed_by}` : ''}</span>}
      </div>
      <p className="mt-1 text-sm font-semibold leading-snug" data-testid={`proposal-title-${p.proposal_id}`}>{p.title}</p>
      {p.lines?.length > 0 && (
        <ul className="mt-1.5 space-y-0.5 text-[12.5px] leading-snug opacity-90">
          {p.lines.map((l, i) => <li key={i} className="whitespace-pre-wrap [overflow-wrap:anywhere]">· {l}</li>)}
        </ul>
      )}
      {p.status === 'confirmed' && p.result?.message && <p className="mt-2 text-[12px] font-medium" data-testid={`proposal-result-${p.proposal_id}`}>{p.result.message}</p>}
      {p.status === 'confirmed' && (p.result?.whatsapp || p.result?.mailto || p.result?.links?.length > 0) && (
        <button type="button" onClick={() => openLinks(p.result)} className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold underline" data-testid={`proposal-open-links-${p.proposal_id}`}>
          <ExternalLink size={12} /> Rouvrir le message
        </button>
      )}
      {!done && (
        <div className="mt-3 flex gap-2">
          <button type="button" disabled={!!busy} onClick={() => act('confirm')} className="inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-xl bg-[var(--p-1c1c1e)] px-3 text-sm font-semibold text-[var(--sur-couleur)] transition-colors hover:bg-black disabled:opacity-60" data-testid={`proposal-confirm-${p.proposal_id}`}>
            {busy === 'confirm' ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Confirmer
          </button>
          <button type="button" disabled={!!busy} onClick={() => act('reject')} className="inline-flex min-h-[40px] items-center justify-center gap-1.5 rounded-xl border border-current/20 bg-white/70 px-3 text-sm font-medium transition-colors hover:bg-[var(--carte)] disabled:opacity-60" data-testid={`proposal-reject-${p.proposal_id}`}>
            <X size={14} /> Ignorer
          </button>
        </div>
      )}
    </div>
  );
};
